import { DestroyRef, Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged, filter } from 'rxjs/operators';
import { QuestionStep } from '../../models/step/question-step.model';
import { Step } from '../../models/step/step.model';
import { QuestionnaireType } from '../../shared/enums/questionnaire-type.enum';
import { StepInterface } from './step.interface';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { StepMapper } from '../../step-mapper';

@Injectable({
  providedIn: 'root',
})
export class DirectStepService {
  private readonly destroyRef: DestroyRef = inject(DestroyRef);

  /**
   * Disable the next and previous button while fetching the next question.
   */
  public isFetching$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    false,
  );

  /**
   * BehaviorSubject containing the directly supplied QuestionData.
   * This BehaviorSubject is supplied through {@see AlfredClientComponent} where
   * it receives data from the client component users.
   */
  private readonly stepSubject$: BehaviorSubject<Step> =
    new BehaviorSubject<Step>(undefined as never);

  /**
   * The parent app can supply the question data which the clientComponent needs
   * to show.
   *
   * Only usable in {@see Mode.DIRECT_HANDLE} mode.
   */
  public set directQuestion(questionData: StepInterface | Step | undefined) {
    if (!questionData) {
      return;
    }

    this.isFetching$.next(true);
    if (questionData instanceof Step) {
      this.stepSubject$.next(questionData);
    } else {
      questionData.position = questionData.position ?? 0;
      questionData.totalQuestions = questionData.totalQuestions ?? 1;

      if (Object.prototype.hasOwnProperty.call(questionData, 'question')) {
        questionData.questionnaireType =
          questionData.questionnaireType ??
          QuestionnaireType.QUESTIONNAIRE_EXAMINATION;
      }

      this.stepSubject$.next(this.denormalizeStep(questionData));
    }

    setTimeout(() => this.isFetching$.next(false), 300);
  }

  /**
   * create Observer which emits the supplied question data which the clientComponent needs
   * to show.
   *
   * Only usable in {@see Mode.DIRECT_HANDLE} mode.
   */
  public get step$(): Observable<Step> {
    return this.stepSubject$.pipe(
      takeUntilDestroyed(this.destroyRef),
      filter((directQuestion) => !!directQuestion),
      distinctUntilChanged((a, b) => {
        if (a instanceof QuestionStep && b instanceof QuestionStep) {
          if (!a?.question || !b?.question) {
            return false;
          }
          return a.question.id === b.question.id;
        }
        return false;
      }),
    );
  }

  /**
   * Method to denormalize StepInterface to step class model.
   */
  private denormalizeStep(response: StepInterface): Step {
    return StepMapper.fromDto(response);
  }
}
